import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  CheckboxContainer,
  CheckboxLabel,
  HeaderSection,
  MainContainer,
  StyledRadio,
} from "../index.styled";
import { ReactComponent as Location } from "../../../Assets/Icons/Location.svg";
import CustomInput from "../../Input";
import { isValidUSZipCode, viewSizeCalculator } from "../../../utils";
import Info from "../../Info";
import { states } from "../../../Configs/config";

const Locations = ({
  data,
  reference,
  filteredData,
  handleInputChange,
  handleRadioChange,
  suggestionsList,
  suggestionsLoader,
  onMatchedHospitalChange,
  onFieldSuggestionsChange,
  isFetching,
  onReset,
}) => {
  const [lastActiveInputName, setLastActiveInputName] = useState(null);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const [selectedRadio, setSelectedRadio] = useState(filteredData.urban_rural_flag || null);

  const handleRadioClick = (e) => {
    const { value } = e.target;

    // If the same radio button is clicked again, unselect it
    if (selectedRadio === value) {
      setSelectedRadio(null);
      handleRadioChange({ target: { name: "urban_rural_flag", value: null } });
    } else {
      setSelectedRadio(value);
      handleRadioChange(e);
    }
  };

  useEffect(() => {
    const handleResize = () => setWindowHeight(window.innerHeight);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Add reset handler
  // const handleReset = () => {
  //   // Reset local state
  //   setLastActiveInputName(null);

  //   // Reset input fields managed by this component
  //   handleInputChange("hospital_name", "");
  //   handleInputChange("cbsa", "");
  //   handleInputChange("zip_code", "");
  //   handleInputChange("state", "");
  //   handleInputChange("county", "");
  //   handleRadioChange({ target: { name: "urban_rural_flag", value: null } }); // Reset radio button

  //   // Call parent reset handler if provided
  //   if (onReset) {
  //     onReset();
  //   }
  // };

  const getFilteredSuggestions = (input, suggestions) => {
    // console.log("", suggestions);
    if (!Array.isArray(suggestions)) return [];
    // Handle potential non-string inputs gracefully
    const lowerInput = String(input || '').toLowerCase(); // Ensure input is a string and handle null/undefined
    return suggestions.filter((item) =>
      String(item || '').toLowerCase().includes(lowerInput) // Ensure item is a string
    );
  };

  const autoFillField = (fieldName, suggestions) => {
    if (suggestions?.length === 1) {
      handleInputChange(fieldName, suggestions[0]);
    }
    onFieldSuggestionsChange?.(fieldName, suggestions);
  };

  // Calculate suggestions based on input - this can stay outside the effect
  const hospitalSuggestions = getFilteredSuggestions(
    filteredData.hospital_name,
    suggestionsList.hospitalNames
  );



  const fields = data.fields;

  // Auto-populate fields when a hospital is selected OR full data becomes available
  useEffect(() => {
    // Calculate matchedHospital INSIDE the effect, only when full data is available
    const matchedHospital =
      hospitalSuggestions.length === 1 && Array.isArray(suggestionsList.fullHospitalData)
        ? suggestionsList.fullHospitalData.find(
          (h) => h.hosp10_name === hospitalSuggestions[0]
        )
        : null;

    if (matchedHospital) {
      // --- Auto-populate other fields (if they are not already set) --- 
      if (matchedHospital.state && !filteredData.state) {
        handleInputChange("state", matchedHospital.state);
      }
      if (matchedHospital.zip_code && !filteredData.zip_code) {
        handleInputChange("zip_code", matchedHospital.zip_code);
      }
      if (matchedHospital.cbsa && !filteredData.cbsa) {
        handleInputChange("cbsa", matchedHospital.cbsa);
      }
      if (matchedHospital.county && !filteredData.county) {
        handleInputChange("county", matchedHospital.county);
      }

      // --- Update urban/rural value only if not already set by user --- 
      if (matchedHospital.urban_rural_flag && !filteredData.urban_rural_flag) {
        // Ensure the name prop is included in the event object for handleRadioChange
        handleRadioChange({ target: { name: "urban_rural_flag", value: matchedHospital.urban_rural_flag } });
      }

      // Notify parent component about the matched hospital
      onMatchedHospitalChange?.(matchedHospital);

    } else {
      // --- Reset Disabled State --- 
      // If no matched hospital (input cleared, data not ready, etc.), reset the flag
    }

  }, [hospitalSuggestions, suggestionsList.fullHospitalData, filteredData, handleInputChange, handleRadioChange, onMatchedHospitalChange]);


  // Effect to auto-fill hospital name when suggestions update and contain only one match
  useEffect(() => {
    const hospitalSuggestions = getFilteredSuggestions(
      filteredData.hospital_name,
      suggestionsList.hospitalNames
    );
    if (hospitalSuggestions.length === 1 && filteredData.hospital_name !== hospitalSuggestions[0]) {
      // Only call autoFillField if the single suggestion isn't already the current value
      // This prevents potential loops if handleInputChange triggers this effect again
      autoFillField("hospital_name", hospitalSuggestions);
    }
    onFieldSuggestionsChange?.("hospital_name", hospitalSuggestions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredData.hospital_name, suggestionsList.hospitalNames]); // Depend only on input value and suggestion list

  // const stateList = Object.entries(states).map(
  //   ([name, code]) => `${code} (${name})`
  // );

  const stateList = Object.keys(states);
  
  useEffect(() => {
    setSelectedRadio(filteredData.urban_rural_flag || null);
  }, [filteredData.urban_rural_flag]);


  const [noMatchFlags, setNoMatchFlags] = useState({
    state: false,
    zip_code: false,
    cbsa: false,
    hospital_name: false,
  });

  const handleNoMatch = (name, hasNoMatch) => {
    setNoMatchFlags((prevFlags) => ({
      ...prevFlags,
      [name]: hasNoMatch,
    }));
  };

  return (
    <MainContainer ref={reference}>
      <HeaderSection>
        <Location />
        {data.category}
      </HeaderSection>
      <ContentWrapper>
        <div style={{ width: "34%" }}>
          <CustomInput
            placeholder={fields[0].placeholder}
            name="hospital_name"
            label={"Hospital or System Name"}
            dropdown={true}
            comboMode={true}
            disabled={isFetching}
            value={filteredData.hospital_name}
            onChange={handleInputChange}
            suggestions={suggestionsList.hospitalNames || []}
            suggestionLoader={suggestionsLoader.hospital_name}
            height={2.2}
            onNoMatch={handleNoMatch}
            filteredData={filteredData}
            lastActiveInputName={lastActiveInputName}
            setLastActiveInputName={setLastActiveInputName}
          />
        </div>
        <RowContainer>
          <div style={{
            width: "48.75%",
            opacity: isFetching ? 0.7 : !!filteredData.hospital_name ? 1 : 1,
          }}>
            <Info
              showArrow={false}
              config={{
                tooltipText: isFetching
                  ? "Bear with us we're fetching the data"
                  : !!filteredData.hospital_name
                    ? "Can't edit as a Hospital has already been selected"
                    : null,
                position: "top",
                offsetY: -10,
              }}
            >
              <CustomInput
                placeholder={fields[1].placeholder}
                name="cbsa"
                label={"CBSA"}
                dropdown={true}
                comboMode={true}
                style={{
                  opacity: isFetching ? 0.1 : 1,
                  pointerEvents: isFetching ? "none" : "auto"
                }}
                disabled={isFetching || !!filteredData.hospital_name}
                value={filteredData.cbsa || ""}
                onChange={handleInputChange}
                suggestions={suggestionsList.cbsa || []}
                suggestionLoader={suggestionsLoader.cbsa}
                height={2.2}
                onNoMatch={handleNoMatch}
                filteredData={filteredData}
                lastActiveInputName={lastActiveInputName}
                setLastActiveInputName={setLastActiveInputName}
              />
            </Info>
          </div>
          <div style={{ width: "48.75%" }}>
            <Info
              showArrow={false}
              config={{
                tooltipText: isFetching
                  ? "Bear with us we're fetching the data"
                  : !!filteredData.hospital_name
                    ? "Can't edit as a Hospital has already been selected"
                    : null,
                position: "top",
                offsetY: -10,
              }}
            >
              <CustomInput
                placeholder={fields[2].placeholder}
                name="zip_code"
                label={"ZIP Code"}
                dropdown={true}
                comboMode={true}
                disabled={isFetching || !!filteredData.hospital_name}
                value={filteredData?.zip_code || ""}
                onChange={handleInputChange}
                suggestions={suggestionsList.zipCode || []}
                error={
                  filteredData?.zip_code &&
                  !isValidUSZipCode(filteredData?.zip_code)
                }
                suggestionLoader={suggestionsLoader.zip_code}
                height={2.2}
                onNoMatch={handleNoMatch}
                filteredData={filteredData}
                lastActiveInputName={lastActiveInputName}
                setLastActiveInputName={setLastActiveInputName}
              />
            </Info>
          </div>
        </RowContainer>
        <RowContainer>
          <div style={{ width: "48.75%" }}>
            <Info
              showArrow={false}
              config={{
                tooltipText: isFetching
                  ? "Bear with us we're fetching the data"
                  : !!filteredData.hospital_name
                    ? "Can't edit as a Hospital has already been selected"
                    : null,
                position: "top",
                offsetY: -10,
              }}
            >
              <CustomInput
                placeholder={fields[3].placeholder}
                name="state"
                label={"State"}
                dropdown={true}
                comboMode={true}
                disabled={isFetching || !!filteredData.hospital_name}
                value={
                  filteredData?.state
                    ? states[filteredData?.state] !== undefined
                      ? `${states[filteredData?.state]} (${filteredData?.state})`
                      : filteredData?.state
                    : ""
                }
                onChange={handleInputChange}
                suggestions={stateList}
                suggestionLoader={suggestionsLoader.state}
                height={2.2}
                onNoMatch={handleNoMatch}
                filteredData={filteredData}
                lastActiveInputName={lastActiveInputName}
                setLastActiveInputName={setLastActiveInputName}
              />
            </Info>
          </div>
          <div style={{ width: "48.75%" }}>
            <Info
              showArrow={false}
              config={{
                tooltipText: isFetching
                  ? "Bear with us we're fetching the data"
                  : !!filteredData.hospital_name
                    ? "Can't edit as a Hospital has already been selected"
                    : null,
                position: "top",
                offsetY: -10,
              }}
            >
              <CustomInput
                placeholder={fields[4].placeholder}
                name="county"
                label={"County"}
                dropdown={true}
                comboMode={true}
                disabled={isFetching || !!filteredData.hospital_name}
                value={filteredData.county || ""}
                onChange={handleInputChange}
                suggestions={suggestionsList.county || []}
                suggestionLoader={suggestionsLoader.county}
                height={2.2}
                onNoMatch={handleNoMatch}
                filteredData={filteredData}
                lastActiveInputName={lastActiveInputName}
                setLastActiveInputName={setLastActiveInputName}
              />
            </Info>
          </div>
        </RowContainer>
        <RowContainer width={"fit-content"}>
          {/* <Info
              showArrow={false}
              config={{
                tooltipText: isFetching
                  ? "Bear with us we're fetching the data"
                  : !!filteredData.hospital_name
                    ? "Can't edit as a Hospital has already been selected"
                    : null,
                position: "top",
                offsetY: -10,
              }}
            > */}
          <CheckboxContainer>
            {/* <input
              style={{
                cursor: (isFetching || !!filteredData.hospital_name) ? "not-allowed" : "pointer",
                accentColor: "#F96250",
                accentColor: (isFetching || !!filteredData.hospital_name) ? "#FFB68F" : "#F96250",
                width: viewSizeCalculator(1.063),
                height: viewSizeCalculator(1.063),
                opacity: !!filteredData.hospital_name ? 0.8 : 1,
              }}
              type="radio"
              name="urban_rural_flag"
              id={fields[6].label}
              value={fields[6].label}
              checked={selectedRadio === fields[6].label}
              onClick={handleRadioClick}
              disabled={isFetching || !!filteredData.hospital_name}
            /> */}
            <StyledRadio
              name="urban_rural_flag"
              id={fields[6].label}
              value={fields[6].label}
              checked={selectedRadio === fields[6].label}
              onClick={handleRadioClick}
              disabled={isFetching || !!filteredData.hospital_name}
              isDimmed={!!filteredData.hospital_name}
            />

            <CheckboxLabel htmlFor={fields[6].label} style={{
              opacity: !!filteredData.hospital_name ? 0.8 : 1,
              cursor: (isFetching || !!filteredData.hospital_name) ? "not-allowed" : "pointer",
            }}>
              {fields[6].label}
            </CheckboxLabel>
          </CheckboxContainer>
          <CheckboxContainer>
            {/* <input
              style={{
                cursor: (isFetching || !!filteredData.hospital_name) ? "not-allowed" : "pointer",
                // accentColor: "#F96250",
                accentColor: (isFetching || !!filteredData.hospital_name) ? "#FFB68F" : "#F96250",
                width: viewSizeCalculator(1.063),
                height: viewSizeCalculator(1.063),
                opacity: !!filteredData.hospital_name ? 0.8 : 1,
              }}
              type="radio"
              name="urban_rural_flag"
              id={fields[7].label}
              value={fields[7].label}
              checked={selectedRadio === fields[7].label}
              onClick={handleRadioClick}
              disabled={isFetching || !!filteredData.hospital_name}
            /> */}
            <StyledRadio
              name="urban_rural_flag"
              id={fields[7].label}
              value={fields[7].label}
              checked={selectedRadio === fields[7].label}
              onClick={handleRadioClick}
              disabled={isFetching || !!filteredData.hospital_name}
              isDimmed={!!filteredData.hospital_name}
            />
            <CheckboxLabel htmlFor={fields[7].label} style={{
              opacity: !!filteredData.hospital_name ? 0.8 : 1,
              cursor: (isFetching || !!filteredData.hospital_name) ? "not-allowed" : "pointer",
            }}>
              {fields[7].label}
            </CheckboxLabel>
          </CheckboxContainer>
          {/* </Info> */}
        </RowContainer>
      </ContentWrapper>
    </MainContainer>
  );
};

export default Locations;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const RowContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 20px;
  width: ${({ width }) => width || "70%"};
`;
